import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Calendar, CheckCircle2, MapPin, MessageCircle, Phone } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { CancellationPolicy } from "@/components/CancellationPolicy";
import { HomeRecoveryPackages } from "@/components/HomeRecoveryPackages";
import { SERVICES_BY_SLUG, type Service } from "@/lib/services";
import { SITE } from "@/lib/site";


const BASE = "https://nsphysiotherapy.co.za";
const URL = `${BASE}/physio-at-home`;
const SERVICE = SERVICES_BY_SLUG["home-physiotherapy"] as Service;

export const Route = createFileRoute("/physio-at-home")({
  component: PhysioAtHome,
  head: () => ({
    meta: [
      { title: "Home Physiotherapy & Home Recovery Packages | Centurion & Midrand" },
      { name: "description", content: "Home physiotherapy in Centurion, Midrand and surrounding areas. Neuro rehab, post op and elderly care at home, plus Home Recovery Packages for ongoing treatment. WhatsApp or book online." },
      { property: "og:title", content: "Home Physiotherapy & Home Recovery Packages | Centurion & Midrand" },
      { property: "og:description", content: "Physiotherapy at your home in Centurion, Midrand and surrounding areas, with structured Home Recovery Packages for consistent rehabilitation. WhatsApp the physiotherapist or book online." },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalTherapy",
          name: SERVICE.title,
          description: SERVICE.metaDescription,
          url: URL,
          provider: { "@id": `${BASE}/#business` },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: SERVICE.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(breadcrumbJsonLd([
          { name: "Home", url: `${BASE}/` },
          { name: "Home Physiotherapy", url: URL },
        ])),
      },
    ],
  }),
});

const neuroGroups: Array<{ title: string; items: string[] }> = [
  { title: "Neurological conditions we treat at home", items: ["Stroke", "Brain injury", "Spinal cord injury", "Parkinson's disease", "Multiple sclerosis", "Peripheral nerve injuries", "Post neurosurgery recovery", "Balance and gait disorders", "Weakness and mobility loss"] },
  { title: "Why home neuro rehab works", items: ["Consistency and repetition in a real life environment", "Caregiver guidance", "Functional mobility training", "Fall prevention in familiar spaces", "Improves independence and confidence"] },
  { title: "What a home visit includes", items: ["Full physiotherapy and neurological assessment", "Gait retraining and balance training", "Strengthening and spasticity management", "Functional retraining and transfers", "Caregiver education", "Home modification advice", "Individualised rehab plan"] },
  { title: "Areas covered", items: ["Centurion (Eco Park, Eco Boulevard, Highveld, Die Hoewes)", "Irene", "Midrand", "Surrounding suburbs and estates"] },
];

function PhysioAtHome() {
  const s = SERVICE;
  const wa = `https://wa.me/${SITE.whatsappIntl}?text=${encodeURIComponent("Hi, I'd like to book a home visit physiotherapy appointment.")}`;
  const packagesWa = `https://wa.me/${SITE.whatsappIntl}?text=${encodeURIComponent("Hi, I'm interested in the home visit packages. Can I please speak to the physiotherapist?")}`;
  const related = s.related
    .map((slug) => SERVICES_BY_SLUG[slug])
    .filter((r): r is Service => Boolean(r));

  return (
    <PageShell>
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Home Physiotherapy" }]} />

      <section className="container-page py-10 md:py-14">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-primary/60">Physio@Home</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold text-primary">{s.title}</h1>
          <p className="mt-5 text-foreground/80 md:text-lg">{s.intro}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/booking" className="btn-hero inline-flex h-11 items-center gap-2 rounded-full px-6 font-semibold">
              <Calendar className="h-4 w-4" /> Book online
            </Link>
            <a href={wa} target="_blank" rel="noreferrer" className="inline-flex h-11 items-center gap-2 rounded-full border border-border px-6 font-semibold text-primary hover:bg-accent">
              <MessageCircle className="h-4 w-4" /> WhatsApp us
            </a>
          </div>
        </div>
      </section>

      <section className="container-page pb-4 md:pb-8">
        <div className="rounded-3xl border border-primary/25 bg-accent/60 p-6 md:p-8">
          <div className="text-xs font-semibold uppercase tracking-widest text-primary/60">Recovery Packages</div>
          <h2 className="mt-2 font-display text-xl md:text-2xl font-bold text-primary">
            Looking for ongoing home physiotherapy? Explore our Recovery Packages
          </h2>
          <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground/75">
            If you require multiple home visits, our structured packages offer better value while encouraging the
            consistency needed for ongoing rehabilitation and chronic condition management.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <a
              href="#recovery-packages"
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary px-7 font-semibold text-primary-foreground hover:opacity-90 sm:w-auto"
            >
              View Home Recovery Packages <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={packagesWa}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border border-border bg-card px-7 font-semibold text-primary hover:bg-accent sm:w-auto"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp the Physiotherapist
            </a>
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-page py-12 grid gap-8 md:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold text-primary">Who we help</h2>
            <ul className="mt-4 space-y-2 text-foreground/80">
              {s.whoWeHelp.map((w) => (
                <li key={w} className="flex gap-2"><CheckCircle2 className="h-5 w-5 shrink-0 text-primary" /> <span>{w}</span></li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-primary">What you'll gain</h2>
            <ul className="mt-4 space-y-2 text-foreground/80">
              {s.benefits.map((b) => (
                <li key={b} className="flex gap-2"><CheckCircle2 className="h-5 w-5 shrink-0" style={{ color: "var(--brand-teal)" }} /> <span>{b}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-page py-12">
          <h2 className="font-display text-2xl font-bold text-primary">Our treatment approach</h2>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {s.approach.map((a) => (
              <div key={a.heading} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-lg font-bold text-primary">{a.heading}</h3>
                <p className="mt-2 text-sm text-foreground/75">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-page py-12 grid gap-8 md:grid-cols-2">
          {neuroGroups.map((g) => (
            <div key={g.title} className="rounded-2xl border border-border bg-card p-6">
              <h2 className="font-display text-xl font-bold text-primary">{g.title}</h2>
              <ul className="mt-4 space-y-2">
                {g.items.map((i) => (
                  <li key={i} className="flex gap-3 text-foreground/85"><CheckCircle2 className="h-5 w-5 shrink-0" style={{ color: "var(--brand-teal)" }} /> {i}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <HomeRecoveryPackages />

      <section className="container-page py-12">
        <div className="grid gap-6 rounded-3xl border border-border p-8 md:p-10 md:grid-cols-2 items-center" style={{ background: "var(--gradient-teal)" }}>
          <div className="text-white">
            <h2 className="font-display text-2xl md:text-3xl font-bold">Pricing</h2>
            <p className="mt-3">Home physiotherapy session: <strong>R1,050</strong></p>
            <p className="mt-1 text-white/90">Medical aid claims submitted at scheme rates. Travel fees may apply depending on distance.</p>
          </div>
          <div className="md:text-right">
            <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="inline-flex h-12 items-center gap-2 rounded-full bg-white px-6 font-semibold text-primary">Book Now on WhatsApp</a>
            <div className="mt-3 flex md:justify-end items-center gap-2 text-white/90 text-sm">
              <Phone className="h-4 w-4" /> <a href={`tel:${SITE.phoneRaw}`}>{SITE.phone}</a>
              <span className="mx-2">·</span>
              <MapPin className="h-4 w-4" /> Centurion &amp; Midrand
            </div>
          </div>
        </div>
      </section>

      <section className="container-page pb-12">
        <CancellationPolicy />
      </section>

      <section className="border-t border-border">

        <div className="container-page py-12 max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-primary">Frequently asked questions</h2>
          <div className="mt-6 divide-y divide-border rounded-2xl border border-border bg-card">
            {s.faqs.map((f) => (
              <details key={f.q} className="group p-5">
                <summary className="cursor-pointer list-none font-semibold text-primary flex items-center justify-between">
                  {f.q}
                  <span className="text-primary/50 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-foreground/75">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="border-t border-border">
          <div className="container-page py-12">
            <h2 className="font-display text-2xl font-bold text-primary">Related services</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to="/services/$slug" params={{ slug: r.slug }} className="group block rounded-2xl border border-border bg-card p-5 hover:border-primary/40">
                  <div className="text-xs font-semibold uppercase tracking-widest text-primary/60">{r.eyebrow}</div>
                  <div className="mt-1 font-display font-bold text-primary">{r.title}</div>
                  <span className="mt-2 inline-flex items-center gap-1 text-sm text-primary group-hover:underline">Learn more <ArrowRight className="h-4 w-4" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </PageShell>
  );
}
